import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card, Input, Button, Form, Switch, Select, Typography, Space, message,
  Alert, Spin, Tag, Empty,
} from 'antd';
import {
  SearchOutlined, ThunderboltOutlined, LinkOutlined, ReloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { trendsAPI } from '../services/api';

const { Title, Text } = Typography;

const SOURCE_OPTIONS = [
  { value: 'reddit', label: 'Reddit' },
  { value: 'x', label: 'X / Twitter' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'hackernews', label: 'Hacker News' },
  { value: 'github', label: 'GitHub' },
  { value: 'polymarket', label: 'Polymarket' },
  { value: 'tiktok', label: 'TikTok (需 ScrapeCreators key)' },
  { value: 'instagram', label: 'Instagram (需 ScrapeCreators key)' },
  { value: 'threads', label: 'Threads (需 ScrapeCreators key)' },
  { value: 'pinterest', label: 'Pinterest (需 ScrapeCreators key)' },
  { value: 'bluesky', label: 'Bluesky' },
  { value: 'perplexity', label: 'Perplexity (需 OpenRouter key)' },
  { value: 'web', label: 'Web (Brave Search)' },
];

export default function TrendsSearchPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [health, setHealth] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  const checkHealth = useCallback(async () => {
    try {
      const res = await trendsAPI.health();
      setHealth(res.data ?? res);
    } catch (e) {
      setHealth({ ok: false, installed: false, error: e.message });
    }
  }, []);

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trendsAPI.listReports({ limit: 10 });
      setRecent((res.data ?? res).reports || []);
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    checkHealth();
    fetchRecent();
    const id = setInterval(fetchRecent, 8000);  // poll for status changes
    return () => clearInterval(id);
  }, [checkHealth, fetchRecent]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const res = await trendsAPI.search({
        topic: values.topic,
        competitors: !!values.competitors,
        include_sources: values.include_sources,
        exclude_sources: values.exclude_sources,
      });
      const r = (res.data ?? res).report;
      message.success(`已開始研究 #${r.id} 「${r.topic}」 — 預估 30-90 秒`);
      fetchRecent();
    } catch (e) {
      message.error(`啟動失敗：${e.message}`);
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <SearchOutlined /> 趨勢搜尋 — Last 30 Days
        </Title>
        <Text type="secondary">
          搜尋過去 30 天內跨平台（Reddit / X / YouTube / HN / GitHub / Polymarket / Web 等）對某主題的真實討論，產出附來源的研究報告。
        </Text>
      </div>

      {health && !health.installed && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="last30days-skill 尚未安裝到 alayaess host"
          description={
            <>
              此功能依賴 alayaess host 上的 openclaw skill。請參考 <Link to="/research/settings">「來源設定」</Link>頁的安裝指引。
              {health.hint && <div style={{ marginTop: 4, fontSize: 12 }}>提示：{health.hint}</div>}
            </>
          }
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="topic"
            label="研究主題"
            rules={[{ required: true, message: '請輸入主題' }]}
            extra="例：「AI 影片生成」「Comboware」「Vibe Coding」「OpenAI 上週動向」"
          >
            <Input.TextArea
              placeholder="輸入要研究的主題、品牌、人物、產品..."
              autoSize={{ minRows: 1, maxRows: 4 }}
            />
          </Form.Item>

          <Form.Item
            name="competitors"
            label="自動比較競品"
            valuePropName="checked"
            tooltip="勾選後會自動發現該主題的 2 個競爭對手做 3 方比較"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="include_sources"
            label="額外啟用的 source（選填）"
            tooltip="付費 source 預設關閉。勾選等於 --include-sources=..."
          >
            <Select mode="multiple" allowClear placeholder="保持預設或挑要加入的"
                    options={SOURCE_OPTIONS.filter(s => /perplexity|youtube_comments/.test(s.value))} />
          </Form.Item>

          <Form.Item
            name="exclude_sources"
            label="排除的 source（選填）"
            tooltip="不想用某些 source 時勾選"
          >
            <Select mode="multiple" allowClear placeholder="保持預設"
                    options={SOURCE_OPTIONS} />
          </Form.Item>

          <Button
            type="primary" size="large" icon={<ThunderboltOutlined />}
            htmlType="submit" loading={submitting}
            disabled={health && !health.installed}
          >
            開始研究
          </Button>
        </Form>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Text strong>最近 10 筆研究</Text>
        <Button size="small" icon={<ReloadOutlined />} onClick={fetchRecent} loading={loading}>重新整理</Button>
      </div>

      {recent.length === 0 ? (
        <Empty description="還沒有任何研究記錄" />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          {recent.map(r => (
            <Card key={r.id} size="small" hoverable
                  onClick={() => navigate(`/research/reports?id=${r.id}`)}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Space>
                    <Tag color={
                      r.status === 'done' ? 'success' :
                      r.status === 'running' ? 'processing' :
                      r.status === 'failed' ? 'error' : 'default'
                    }>
                      {r.status === 'done' && <CheckCircleOutlined />}
                      {r.status === 'failed' && <CloseCircleOutlined />}
                      {r.status === 'running' && <Spin size="small" style={{ marginRight: 4 }} />}
                      {' '}{r.status}
                    </Tag>
                    {r.competitors && <Tag>競品比較</Tag>}
                    {r.run_seconds && <Tag>{r.run_seconds}s</Tag>}
                  </Space>
                  <div style={{ fontWeight: 600, marginTop: 4 }}>{r.topic}</div>
                  {r.summary && (
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                      {r.summary.slice(0, 120)}{r.summary.length > 120 ? '...' : ''}
                    </Text>
                  )}
                  {r.error && (
                    <Text type="danger" style={{ fontSize: 12, display: 'block' }}>
                      ❌ {r.error}
                    </Text>
                  )}
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {new Date(r.created_at).toLocaleString('zh-TW')}
                  </Text>
                </div>
                <Button size="small" icon={<LinkOutlined />}>看報告</Button>
              </Space>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
}
